import { LocalStorageService } from '@uplift/services';
import type { CheckboxGroupOption } from './CheckboxGroup.component';
import { useMemo, useState } from 'react';

type UsePreferNotToSayProps = {
  name: string;
  options: (CheckboxGroupOption | string)[];
  includePreferNotToSay?: boolean;
  value: unknown[];
  setValue: (value: unknown[], shouldValidate?: boolean) => void;
};

export const PREFER_NOT_TO_SAY = 'preferNotToSay';

/**

    This hook is used to manage the state of the "Prefer not to say" checkbox in a CheckboxGroup component.
    It will add the "Prefer not to say" option to the options array if includePreferNotToSay is true,
    though it will not add the option to the field value formik state.

    Instead of adding the "Prefer not to say" option to the formik state, it will be stored in the local storage
    to persist the value across page refreshes. This is done to avoid having to add a "prefer not to say" option
    to the backend services for the fields that require it. Since this option is only used for UX purposes,
    using a local storage value is sufficient and prevents unnecessary complexity in the backend.

    Usage:
    ```
    const checkboxGroupProps = usePreferNotToSay({
        name,
        options,
        includePreferNotToSay: true // true on fue flow, false on profile page
    });
    ```

*/
export const usePreferNotToSay = (props: UsePreferNotToSayProps) => {
  const LOCAL_STORAGE_KEY = `${props.name}_prefer_not_to_say`;
  const [ preferNotToSay, setPreferNotToSay ] = useState(() => {
    if (props.includePreferNotToSay) {
      return LocalStorageService.getItem<boolean>(LOCAL_STORAGE_KEY) ?? false;
    }

    return false;
  });

  const value = [ ...props.value ].concat(preferNotToSay ? [ PREFER_NOT_TO_SAY ] : []);

  const setValue = (value: unknown[], shouldValidate?: boolean) => {
    if (value.includes(PREFER_NOT_TO_SAY)) {
      setPreferNotToSay(true);
      props.setValue([], shouldValidate);
    } else {
      setPreferNotToSay(false);
      props.setValue(value, shouldValidate);
    }

    LocalStorageService.setItem(LOCAL_STORAGE_KEY, value.includes(PREFER_NOT_TO_SAY));
  };

  const options = useMemo(() => {
    if (props.includePreferNotToSay) {
      let options = props.options.map((opt) => {
        return typeof opt === 'string' ? { label: opt, value: opt } : opt;
      });

      if (preferNotToSay) {
        options = options.map(o => ({ ...o, disabled: true }));
      }

      return options.concat({ label: 'Prefer not to say', value: PREFER_NOT_TO_SAY });
    }

    return props.options;
  }, [ preferNotToSay, props.includePreferNotToSay, props.options ]);

  return { value, setValue, options };
};
